import { Json } from "@redotech/json/json";
import { GetUser } from "@redotech/redo-model/user";
import {
  Equal,
  arrayEqual,
  objectEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";
import type { Stripe } from "stripe";
import { AdLinkType } from "./ad-link";
import { ConversationTag } from "./conversation";
import { Coverage, PaymentModel } from "./coverage";
import { DiscountSettings } from "./order-discount";
import type { PaymentMethod } from "./payout";
import { PriceOption, SkuOption } from "./return-flow/condition";

export const enum LocationCondition {
  PRODUCT_TAGS = "product_tags",
  DAMAGED = "damaged",
  NAME = "name",
  MULTIPLE_CHOICE = "multiple_choice",
  RETURN_REASON = "return_reason",
  COLLECTIONS = "collections",
  ORDER_TAGS = "order_tags",
  CUSTOMER_TAGS = "customer_tags",
  DISCOUNTS = "discounts",
  PRICE = "price",
  SKU = "sku",
  SALES_CHANNEL = "sales_channel",
  CUSTOMER_COUNTRY = "customer_country",
}

export enum ShippingInsurance {
  DISABLED = "disabled",
  ENABLED = "enabled",
}

export interface GetTeam {
  roles: string[];
  _id: string;
  email: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  name: string;
  updatedAt: string;
  images: string[];
  updatePermissionsLink?: string;
  team: Team;
}

export type AttachmentStrategy =
  | "checkbox"
  | "single-toggle"
  | "double-toggle"
  | "drop-down"
  | "check-out"
  | "double-check-out"
  | "cart-card";

export type LabelExpirationStrategy = "return" | "order" | "delivery";

export interface PriceBracket {
  value: number;
  pricePoint: number;
}
export interface PricingRuleSet {
  type: "fixed" | "percentage";
  countries: string[];
  priceBrackets: PriceBracket[];
}

export interface DynamicReturnsPricing {
  pricePerAdditonalItem: number;
  maxPrice: number;
}

interface ProcessingAutomation {
  exchanges: ProcessEvent;
  refunds: ProcessEvent;
  store_credit: ProcessEvent;
  exchanges_delay: number;
  refunds_delay: number;
  store_credit_delay: number;
}

export interface StripeData {
  customer_id: string;
  card?: Stripe.Card | null;
  paymentMethod?: PaymentMethod;
  secondaryPaymentMethod?: PaymentMethod;
}

export interface Team {
  address: Address;
  accessToken: string;
  storefrontAccessToken: string;
  accessScope: string[];
  billingFailure: "allow" | "restrict";
  billingMethod: string;
  customer_email: string;
  damagedAddress?: Address;
  automation: {
    enable: boolean;
    returnProcessing: ProcessingAutomation;
    claimProcessing: ProcessingAutomation;
    fulfill_redo_item: string;
    returnOrderTags: {
      onRefund: string[];
    };
  };
  failed_payouts: number;
  shutdown_date: string;
  _id: string;
  email: string;
  adminName: string;
  adminPhone: string;
  intercomId: string;
  lastOrderSync: string;
  orderBackfillWatermark: string;
  orderBackfillMin: string;
  merchantPricePerOrder?: string;
  name: string;
  onboarding: Onboarding;
  notification_email: string;
  notify_on_return_created: boolean;
  notify_on_delivery: boolean;
  notify_on_needs_review: boolean;
  claim_notification_email: string;
  notify_on_claim_created: boolean;
  notify_on_claim_delivery: boolean;
  notify_on_claim_needs_review: boolean;
  portal: Portal;
  pricePerOrder: string;
  receipt_email: string;
  returnLocationId?: string;
  settings: Settings;
  storeUrl: string;
  stripe: StripeData;
  users: TeamUser[];
  widget_slug: string;
  _shopify: Record<string, any>;
  createdAt: string;

  save(): Promise<void>;
  getStorefrontAccessToken(): Promise<string | null>;
}

export interface DateRange {
  start: string | null;
  end: string | null;
}

export enum DiscountDistributionMethod {
  FAMILY = "DistributeAcrossFamily",
  ORDER = "DistributeAcrossOrder",
  LINE_ITEM = "LineItem",
}

export interface PagesData {
  pageId: string;
  pageName: string;
  pageAccessToken: string;
  instagramBusinessAccountId?: string;
  instagramUsername?: string;
}

export interface QuickLink {
  title: string;
  url: string;
}

export interface PackageProtectionSettings {
  enabled: boolean;
  coverage?: boolean;
  customerClaimsEnabled?: boolean;
  minPrice: number;
  maxPrice: number;
  percentage: number;
  splitProducts: boolean;
  pricingRuleSet?: PricingRuleSet[];
}

export interface Settings {
  storeUrl: string;
  discountDistributionMethod: DiscountDistributionMethod;
  showPDFInvoiceButton: boolean;
  inventory: {
    minimum_stock: number;
    restock: boolean;
    restockDamaged: boolean;
  };
  labelExpiration: {
    days: number;
    reminderEmails: boolean;
    expirationEmails: boolean;
    reminderEmailDays: number[];
    strategy: LabelExpirationStrategy;
  };
  chatFlow: Json;
  checkboxFontSize?: number;
  checkboxSelector?: string;
  checkboxText?: string;
  claimFlow: Json;
  emailFlows?: {
    _id: string;
    flow: Json;
  }[];
  checkboxTextAfterRedo?: string;
  countries: string[];
  countriesPackageProtection: string[];
  coverage: Coverage;
  coverageEnabled: boolean;
  coverageExcludeCollections: string[];
  coverageExcludeCustomerTags: string[];
  coverageExcludeSources: string[];
  coverageExcludeTags: string[];
  coverageExcludeProductTags: string[];
  coverageExcludeDiscountCodes: string[];
  coverageExcludeProperties: string[];
  excludeCoverageIfAnyInvalidProduct?: boolean;
  discountCodePartialMatch?: boolean;
  createdAt: string;
  customerSupport: {
    wrongProduct: CustomerSupportSetting;
    damagedItem: CustomerSupportSetting;
  };
  enabled: boolean;
  exchanges: {
    advancedExchanges: AdvancedExchanges;
    allowInstant: boolean;
    notes: { enabled: boolean };
    suffix: string | null;
    orderName: {
      prefix: string;
      suffix: string;
      useShopifyGeneratedName: boolean;
    };
    outOfStockPolicy: RefundType;
    excessExchangeValue: RefundType;
    outOfStockAdvanced: OutOfStockAdvancedOption;
    excludeProductTags: string[];
    shopSiteURL: string;
    shopSiteDomain: string;
    enableOnHoldOrders: boolean;
    differentPricedVariantExchanges: boolean;
    variantExchangeProductTags: string[];
    usePreDiscountPrice: boolean;
  };
  excludeVariantExchanges: boolean;
  failed_payouts: number;
  hideCheckboxBranding: boolean;
  hidePortalBranding: boolean;
  hideCheckbox: boolean;
  images: string[];
  locations: Location[];
  merchantCoverage: Coverage;
  returnAdjustment: {
    positiveName: string;
    negativeName: string;
  };
  paidModel: PaymentModel;
  policyType: string;
  productName?: string;
  receipt_email: string;
  redoAutoCheck: boolean;
  packageProtectionAutoCheck: boolean;
  returnFlow: Json;
  finalizeReturnFlow: Json;
  finalizeClaimFlow: Json;
  returnLocationId: string;
  returnPortal: string;
  returnPortalHeaderText?: string;
  returnPortalButtonText?: string;
  shippingCarrierOverride: string[];
  shippingInsurance: ShippingInsurance;
  shippingServiceOverride: Map<string, string[]>;
  shopAgainAction: "gift_card" | "discount_code" | "rise";
  riseTrigger: { url: string } | null;
  sku?: string;
  html?: string;
  updatedAt: string;
  upsert: { enabled: boolean; value: string; type: string };
  widget: { url: string };
  packingSlipEnabled?: boolean;
  deductLabelFromCredit?: boolean;
  deductLabelFromRefund?: boolean;
  cartToggleEnabled: boolean;
  cartToggleSelector?: string;
  cartClickListenerSelector?: string;
  cartTogglePlacement?: "before" | "after" | "prepend" | "append";
  packingInstructions?: string[];
  exchangeGroups: ExchangeGroup[];
  shopifyExtension?: {
    hideRedoProduct?: HideRedoConfig;
    customizationVisible?: boolean;
    customizeCartItem?: boolean;
    brandPrefix?: string;
    logo?: string;
    returnNameOverride?: string;
    packageProtectionNameOverride?: string;
    bothProductNameOverride?: string;
    checkoutExtensionEnabled?: boolean;
    attachmentStrategy?: AttachmentStrategy;
    toggleSubtextEnabled?: boolean;
    toggleTextOptions?: {
      returnToggle: ToggleFields;
      packageProtectionToggle: ToggleFields;
      bothProductToggle: ToggleFields;
    };
    infoIconLink?: string;
    customPdpCss?: string;
    customToggleCss?: string;
    forceIPAddressLocation?: boolean;
    coverageExcludePickup?: boolean;
    usingCartAndCheckoutToggle?: boolean;
  };
  returns?: {
    enabled: boolean;
    refundAllReturnedItems: boolean;
    dynamicPricing?: DynamicReturnsPricing;
    settlementEnabled: boolean;
    settlementRefund?: number;
  };
  packageProtection?: PackageProtectionSettings;
  support?: {
    receiveEmail?: string;
    senderEmails?: SenderEmail[];
    emailSignature?: string;
    tags?: ConversationTag[];
    // TODO: remove
    bccEmails?: boolean;
    tagSpam?: boolean;
    ticketAssignment: "manual" | "balanced" | "round-robin";
    usersForAutoAssignment: string[];
    autoAssignOnResponse?: boolean;
    maxTicketsPerUser: string;
    metaPagesData?: {
      userId?: string;
      longLivedToken?: string;
      pagesData?: PagesData[];
    };
    excludedEmails?: string[];
    widgetQuickLinks?: QuickLink[];
    chatExpirationMinutes?: number;
    chatExpirationMessage?: string;
    messagingHeadingText?: string;
    homeHeadingText?: string;
    headingLogoUrl?: string;
    hideWidgetAfterHours?: boolean;
    propagateDeletes?: boolean;
    widgetFontFamily?: string;
    widgetColor?: string;
    widgetPosition?: "left" | "right";
    xAdjustment?: string;
    yAdjustment?: string;
    widgetHoursEnabled?: boolean;
    widgetHoursTimezone?: string;
    widgetHours?: {
      sunday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
      monday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
      tuesday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
      wednesday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
      thursday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
      friday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
      saturday: {
        enabled: boolean;
        start: string | null;
        end: string | null;
      };
    };
  };
  notifyCustomerDefault: boolean;
  merchantPaidEnabledDates?: DateRange[];
  modalTextAdjustments: {
    text1?: string;
    title1?: string;
    text2?: string;
    title2?: string;
    text3?: string;
    title3?: string;
  };
  labelPriceAdjustment?: number | null;
  labelPriceExcludeCoverage?: boolean;
  minimumLabelOunces?: number;
  defaultLineItemOunces?: number;
  toggleFontSize?: number;
  checkoutSelector?: string;
  submitButtonSelector?: string;
  billingSettings: BillingSettings;
  bundleRulesList: BundleRules[];
  pickupEnabled: boolean;
  shopifyPixelId?: string;
  createShopifyReturns?: boolean;
  instantRefundEnabled?: boolean;
  orderTracking: {
    enabled: boolean;
    createTrackers: boolean;
    orderDiscount: {
      enabled: boolean;
      settings: DiscountSettings;
    };
    showPDPDeliveryEstimate?: boolean;
    ads: {
      imageUrl: string;
      url: string;
      altText: string;
      type: AdLinkType;
      showOnlyNonSubscribed: boolean;
    }[];
    upsellProducts: {
      source: "recommendations" | "collection";
      collectionId?: number;
    };
  };
  variantExchangeTitle?: string;
  allowPreProtectedOrders?: boolean;
}

export interface HideRedoConfig {
  enabled: boolean;
  productSelector?: string;
  cartCountModifications?: CartCountModification[];
  cartPriceModifications?: CartCountModification[];
  cartDiscountModifications?: CartCountModification[];
}

export interface CartCountModification {
  selector: string;
  search: string;
  replace: string;
}

export interface ToggleFields {
  titleText: string;
  positiveSubtext: string;
  negativeSubtext: string;
}

export interface CollectionInfo {
  id: string;
  name: string;
  kind: "smart" | "custom";
}

export type ExchangeGroupType =
  | "tag:tag"
  | "tag:collection"
  | "name:collection"
  | "collection:collection";

export type PriceDifferenceType = "ignore" | "apply";

export interface BaseExchangeGroup {
  type: ExchangeGroupType;
  groupName: string;
  priceDifference: PriceDifferenceType;
}

export interface TagForTagGroup extends BaseExchangeGroup {
  type: "tag:tag";
  sourceTags: string[];
  targetTags: string[];
}

export interface NameForCollectionGroup extends BaseExchangeGroup {
  type: "name:collection";
  sourceName: string;
  targetCollection: CollectionInfo;
}

export interface TagForCollectionGroup extends BaseExchangeGroup {
  type: "tag:collection";
  sourceTags: string[];
  targetCollection: CollectionInfo;
}

export interface CollectionForCollectionGroup extends BaseExchangeGroup {
  type: "collection:collection";
  sourceCollection: CollectionInfo;
  targetCollection: CollectionInfo;
}

export type ExchangeGroup =
  | TagForTagGroup
  | NameForCollectionGroup
  | TagForCollectionGroup
  | CollectionForCollectionGroup;

export const exchangeGroupEqual: Equal<ExchangeGroup> =
  objectEqual<ExchangeGroup>({
    type: stringEqual,
    groupName: stringEqual,
    priceDifference: stringEqual,
    sourceName: optionalEqual(stringEqual),
    sourceTags: optionalEqual(arrayEqual(stringEqual)),
    targetTags: optionalEqual(arrayEqual(stringEqual)),
    sourceCollection: optionalEqual(
      objectEqual<CollectionInfo>({
        id: stringEqual,
        name: stringEqual,
        kind: stringEqual,
      }),
    ),
    targetCollection: optionalEqual(
      objectEqual<CollectionInfo>({
        id: stringEqual,
        name: stringEqual,
        kind: stringEqual,
      }),
    ),
  });

export type AdvancedExchanges = "all" | "variant_only" | "less_than_equal";

export type Onboarding = any;

export interface Address {
  name: string;
  street1: string;
  street2?: string;
  city: string;
  zip: string;
  state?: string;
  country: string;
  country_name?: string;
  phone?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerSupportSetting {
  ask: boolean;
  ifYes: string;
  then: string;
}

export interface Location {
  flowTypes: string[];
  condition: LocationCondition | null;
  multipleChoiceQuestion: string | null;
  multipleChoiceQuestionAnswers: string[] | null;
  returnReasons: string[] | null;
  originalLocation: boolean;
  address?: Address;
  locationId: string;
  tags: string[] | null;
  collections: string[] | null;
  discounts: string[] | null;
  priceMatchType: PriceOption | null;
  price: string | null;
  skuMatchType: SkuOption | null;
  skus: string[] | null;
  salesChannels: string[] | null;
  countries: string[] | null;
}

export interface Portal {
  _id: string;
  font_family: string;
  title_font_family: string;
  body_font_family: string;
  shippingFeeText: string;
  custom_css: string;
  font_weight: 100 | 300 | 400 | 500 | 700 | 900;
  button_color: string;
  accent_color: string;
  accent_background_color: string;
  home_text_color: string;
  enable_alert: boolean;
  notification: string;
  returnButtonText: string;
  claimButtonText: string;
  notExchangeButtonText?: string;
  exchangeButtonText?: string;
  standard_exchange_text?: string;
  hideDiscountCodes?: boolean;
  instant_exchange_text: string;
  privacy_link: string;
  background_url: string | null;
  favicon_url: string | null;
  logo_url: string | null;
  colors: string[];
  injection_text_color: string;
  header?: string;
  footer?: string;
  green_return_text?: string;
  custom_confirmation_text?: string;
  green_return_confirmation_text?: string;
  green_return_confirmation_description?: string;
}

export interface TeamUser {
  roles: string[];
  _id: string;
  user: string | GetUser;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export interface RenderedTeamUser {
  roles: string[];
  _id: string;
  user: GetUser;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export type RefundType = "store_credit" | "refund";
export type OutOfStockAdvancedOption = "continue" | "cancel";

/**
 * Which type of return Status the automation will wait for to automatically process the return/claim.
 * @see import("./return").Status
 */
export type ProcessEvent =
  | "complete" // None (manually process)
  | "open" // Return created
  | "in_transit" // Shipment in transit
  | "delivered"; // Shipment delivered

export interface SenderEmail {
  email: string;
  name: string;
  postmarkId: number;
  dkimHost: string;
  dkimText: string;
  returnPathDomain: string;
  returnPathValue: string;
  setupStatus: string;
}

export interface BillingSettings {
  schedule: "bimonthly" | "weekly";
  dayOfWeek?: number;
  firstBillingDayOfMonth?: number;
}

export interface TeamInfo {
  id: string;
  url: string;
  logoUrl: string;
  chatHeadingLogoUrl: string;
  customerPortalUrl: string;
  customerPortalButtonText?: string;
  messagingHeadingText: string;
  homeHeadingText: string;
  name: string;
  chatFlow: Json;
  widgetQuickLinks?: QuickLink[];
  hideWidgetAfterHours?: boolean;
  widgetColor?: string;
  widgetFontFamily?: string;
  widgetPosition?: "left" | "right";
  xAdjustment?: string;
  yAdjustment?: string;
  widgetHoursTimezone?: string;
  widgetHoursEnabled?: boolean;
  widgetHours?: {
    sunday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    monday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    tuesday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    wednesday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    thursday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    friday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
    saturday?: {
      enabled: boolean;
      start: string;
      end: string;
    };
  };
}

export type BundleValueType = "sku" | "variantId" | "bundleId";

export type BundleType = "expand" | "merge";

export interface BundleRules {
  key: string;
  valueType: BundleValueType;
  type: BundleType;
  valueDelimiter: string;
  valueParser: string;
}
